import { api } from './client';
import type { ImportResult } from './items';

/** Accounts books: groups, ledgers, voucher types and Receipt / Payment / Contra / Journal vouchers. */

export type LedgerKind = 'cash' | 'bank' | 'party' | 'other';
export type VoucherType = 'Receipt' | 'Payment' | 'Contra' | 'Journal' | 'Sales' | 'Purchase' | 'Credit Note' | 'Debit Note';
export type RefType = 'Agst Ref' | 'New Ref' | 'Advance' | 'On Account';
/** Bank Allocation transaction types: Receipt / Payment, and Contra (which adds Cash Deposit). */
export const BANK_MODES = ['NEFT', 'RTGS', 'IMPS', 'UPI', 'Cheque'] as const;
export const CONTRA_MODES = ['Cheque', 'Cash Deposit', 'NEFT', 'RTGS', 'IMPS'] as const;

/** YYYY-MM-DD → DD-MM-YYYY. */
export const dmy = (d: string | null | undefined) => (d ? `${d.slice(8, 10)}-${d.slice(5, 7)}-${d.slice(0, 4)}` : '');

// ── Groups ──
export interface AccGroup {
  id: number; name: string; parentId: number | null; parentName: string | null;
  nature: string; isSystem: boolean; ledgers: number; children: number;
}
export interface GroupInput { name: string; parentId: number | null; nature?: string | null }

export const listGroups = () => api.get<AccGroup[]>('/accounts/books/groups').then((r) => r.data);
export const createGroup = (b: GroupInput) => api.post('/accounts/books/groups', b).then((r) => r.data);
export const alterGroup = (id: number, b: GroupInput) => api.patch(`/accounts/books/groups/${id}`, b).then((r) => r.data);
export const deleteGroup = (id: number) => api.delete(`/accounts/books/groups/${id}`).then((r) => r.data);

// ── Ledgers ──
export interface AccLedger {
  id: number; name: string; alias: string | null; groupId: number; group: string; nature: string; kind: LedgerKind;
  partyId: number | null; partyCode: string | null; isSystem: boolean;
  /** Signed: + Dr, − Cr. */
  opening: number; closing: number; entries: number; bills: number;
  /** Expense heads: the expense bill's defaults. */
  gstRate: number | null; tdsSection: string | null; costCentreId: number | null; itcBlocked: string | null;
}
export interface LedgerOption {
  id: number; name: string; group: string; kind: LedgerKind; partyCode: string | null;
  /** D = customer (Sundry Debtors), C = supplier (Sundry Creditors). */
  role: 'D' | 'C' | null;
  /** Current balance, signed: + Dr, − Cr. */
  balance: number;
  bank: { account: string; ifsc: string | null; name: string | null } | null;
  /** Party ledgers: GSTIN, state code (place of supply) and credit limit (0 = none). */
  gstin: string | null; stateCode: string | null; creditLimit: number;
  /** Suppliers: PAN (company if its 4th letter is C) and credit days. */
  pan: string | null; creditDays: number | null;
  /** Expense heads (under Indirect Expenses): the expense bill's defaults. */
  expense: { gstRate: number | null; tdsSection: string | null; costCentreId: number | null; itcBlocked: string | null } | null;
}
export interface LedgerInput {
  name: string; alias: string | null; groupId: number; opening: number; side: 'Dr' | 'Cr';
  gstRate?: number | null; tdsSection?: string | null; costCentreId?: number | null; itcBlocked?: string | null;
}

export const listLedgers = () => api.get<AccLedger[]>('/accounts/books/ledgers').then((r) => r.data);
export const ledgerOptions = () => api.get<LedgerOption[]>('/accounts/books/ledger-options').then((r) => r.data);
export const createLedger = (b: LedgerInput) => api.post('/accounts/books/ledgers', b).then((r) => r.data);
export const alterLedger = (id: number, b: LedgerInput) => api.patch(`/accounts/books/ledgers/${id}`, b).then((r) => r.data);
export const deleteLedger = (id: number) => api.delete(`/accounts/books/ledgers/${id}`).then((r) => r.data);

export interface LedgerVouchers {
  ledger: { id: number; name: string; group: string };
  opening: number; closing: number;
  rows: { voucherId: number; date: string; type: string; voucherNo: string; particulars: string; narration: string | null; debit: number; credit: number; balance: number }[];
}
export const getLedgerVouchers = (id: number, q: { from?: string; to?: string }) =>
  api.get<LedgerVouchers>(`/accounts/books/ledgers/${id}/vouchers`, { params: q }).then((r) => r.data);

export interface PendingBill { billNo: string; billDate: string | null; dueDate: string | null; /** + we are owed, − we owe */ pending: number }
export const getPending = (ledgerId: number, excludeVoucher?: number) =>
  api.get<{ bills: PendingBill[]; onAccount: number }>(`/accounts/books/ledgers/${ledgerId}/pending`, { params: { excludeVoucher } }).then((r) => r.data);

export const OPENING_TEMPLATE_URL = '/accounts/books/opening/template.xlsx';
export async function importOpening(file: File): Promise<ImportResult> {
  const fd = new FormData();
  fd.append('file', file);
  const r = await api.post<ImportResult>('/accounts/books/opening/import', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 5 * 60 * 1000 });
  return r.data;
}

// ── Voucher types ──
export interface AccVoucherType {
  id: number; name: string; abbr: string; prefix: string; startNo: number; width: number; lastNo: number;
  vouchers: number; nextNo: string;
}
export const listVoucherTypes = () => api.get<AccVoucherType[]>('/accounts/books/voucher-types').then((r) => r.data);
export const alterVoucherType = (id: number, b: { abbr: string; prefix: string; startNo: number; width: number }) =>
  api.patch(`/accounts/books/voucher-types/${id}`, b).then((r) => r.data);

// ── Vouchers ──
export interface BillLine { refType: RefType; billNo?: string | null; amount: number; dueDate?: string | null }
export interface VoucherLine { ledgerId: number; side: 'Dr' | 'Cr'; amount: number; bills?: BillLine[] | null; costCentreId?: number | null }

// ── TDS Nature of Payment, Cost Centres, Expense bill ──
export interface TdsSection {
  code: string; nature: string; oldSection: string | null; kind: 'normal' | 'rent' | 'goods'; rateCompany: number; rateOther: number;
  singleLimit: number | null; yearLimit: number | null; monthLimit: number | null; ledger: string; isSystem: boolean;
}
export type TdsInput = { code?: string; nature: string; oldSection: string | null; rateCompany: number; rateOther: number; singleLimit: number | null; yearLimit: number | null };
export const listTdsSections = () => api.get<TdsSection[]>('/accounts/books/tds-sections').then((r) => r.data);
export const createTdsSection = (b: TdsInput) => api.post('/accounts/books/tds-sections', b).then((r) => r.data);
export const alterTdsSection = (code: string, b: TdsInput) => api.patch(`/accounts/books/tds-sections/${encodeURIComponent(code)}`, b).then((r) => r.data);
export const deleteTdsSection = (code: string) => api.delete(`/accounts/books/tds-sections/${encodeURIComponent(code)}`).then((r) => r.data);

export interface CostCentre { id: number; name: string; entries: number; amount: number }
export const listCostCentres = () => api.get<CostCentre[]>('/accounts/books/cost-centres').then((r) => r.data);
export const createCostCentre = (name: string) => api.post('/accounts/books/cost-centres', { name }).then((r) => r.data);
export const alterCostCentre = (id: number, name: string) => api.patch(`/accounts/books/cost-centres/${id}`, { name }).then((r) => r.data);
export const deleteCostCentre = (id: number) => api.delete(`/accounts/books/cost-centres/${id}`).then((r) => r.data);

export type PayVia = 'credit' | 'bank' | 'cash' | 'split';
export interface ExpenseInput {
  date: string; headLedgerId: number; costCentreId: number | null; taxable: number; gstRate: number; claimItc: boolean;
  gstAmount: number | null; tdsAmount: number | null;
  vendorLedgerId: number | null; billNo?: string; billDate?: string; tdsSection: string | null;
  paidTo?: string; stateCode?: string | null;
  payVia: PayVia; bankLedgerId?: number | null; cashLedgerId?: number | null; mode?: string; refNo?: string; bankAmount?: number | null; narration?: string;
}
/** An expense bill's header, as saved on its voucher (kind 'exp'). */
export interface ExpenseDetails {
  kind: 'exp' | 'exp-payment'; vendorLedgerId: number | null; tdsSection: string | null; head: string; headLedgerId: number; costCentreId: number | null;
  vendor: string | null; paidTo: string; billNo: string | null; billDate: string | null; dueDate: string | null; stateCode: string | null; intra: boolean; registered: boolean;
  taxable: number; gstRate: number; gst: number; gstAuto: number; cgst: number; sgst: number; igst: number; itc: boolean; itcWhy: string | null; total: number;
  tds: number; tdsAuto: number; tdsBase: number; tdsRate: number; tdsReason: string | null; pan: string | null; company: boolean | null; net: number;
  payVia: PayVia; bankPart: number; cashPart: number;
}
export interface TdsPreview { section: string; base: number; rate: number; tds: number; reason: string; cum: number; company: boolean; pan: string | null }
export const tdsPreview = (q: { vendor: number; sec: string; taxable: number; date: string; excludeVoucher?: number }) =>
  api.get<TdsPreview>('/accounts/books/tds-preview', { params: q }).then((r) => r.data);
export const createExpense = (b: ExpenseInput) => api.post<{ bill: VoucherFull; payments: VoucherFull[] }>('/accounts/books/expense', b).then((r) => r.data);
export const alterExpense = (id: number, b: ExpenseInput) => api.put<VoucherFull>(`/accounts/books/vouchers/${id}`, b).then((r) => r.data);
export interface VoucherInput {
  date: string; narration?: string; mode?: string; refNo?: string; instrDate?: string | null;
  bankName?: string; favouring?: string; lines: VoucherLine[];
}
export interface VoucherRow {
  id: number; type: VoucherType; voucherNo: string; date: string; status: 'Active' | 'Cancelled'; amount: number;
  narration: string | null; mode: string | null; refNo: string | null; instrDate: string | null;
  bankName: string | null; favouring: string | null; version: number;
  debit: string[]; credit: string[]; against: string[];
  /** The cash / bank account(s) on the voucher (registers only). */
  cashBank: string[];
  /** Sales / Purchase: the invoice header; Credit / Debit Note: the note's header. */
  details: (TradeDetails & Partial<NoteDetails> & Partial<ExpenseDetails>) | null; createdByName: string | null; updatedAt: string;
}
/** Sales / Purchase invoice header, as saved. */
export interface TradeDetails {
  partyLedgerId: number; party: string; gstin: string | null; stateCode: string | null; ledgerId: number; ledger: string;
  intra: boolean; registered: boolean; creditDays: number; dueDate: string; roundOff: boolean;
  taxable: number; cgst: number; sgst: number; igst: number; roundOffAmt: number; total: number;
  orderNo?: string | null; orderDate?: string | null; irn?: string | null; ackNo?: string | null; ewayBillNo?: string | null; vehicle?: string | null; km?: number | null;
  billNo?: string; billDate?: string; tds?: boolean; tdsAmount?: number; tdsBase?: number; tdsReason?: string; payable?: number;
  pd: Record<string, string>;
}
export interface TradeItem { itemId: number | null; name: string; hsn: string; qty: number; unit: string; rate: number; disc: number; gst: number; amount?: number }
export interface TradeInput {
  date: string; partyLedgerId: number; ledgerId: number; items: TradeItem[]; creditDays: number; roundOff: boolean; narration?: string;
  orderNo?: string; orderDate?: string | null; irn?: string; ackNo?: string; ewayBillNo?: string; vehicle?: string; km?: number;
  billNo?: string; billDate?: string; tds?: boolean; pd?: Record<string, string>;
}
/** Credit Note (cn goods returned · rd rate difference) / Debit Note (dn) header, as saved. */
export type NoteKind = 'cn' | 'rd' | 'dn';
export interface NoteDetails {
  againstId: number; partyLedgerId: number; kind: NoteKind; against: string; againstBill: string; againstDate: string; party: string; reason: string; intra: boolean;
  taxable: number; cgst: number; sgst: number; igst: number; total: number; agst: number; onAccount: number;
  lines: { srcLine: number; qty: number; diff?: number }[];
}
export const NOTE_REASONS: Record<NoteKind, string[]> = {
  cn: ['Goods returned by customer', 'Damaged in transit', 'Quality rejection'],
  rd: ['Rate difference', 'Post-sale discount', 'Volume / scheme discount'],
  dn: ['Goods returned to supplier', 'Short supply', 'Damaged goods', 'Rate difference'],
};
/** An invoice / bill a note can go against, with what is still open on each line. */
export interface NoteDoc {
  id: number; voucherNo: string; billNo: string; date: string; total: number; intra: boolean; registered: boolean; partyLedgerId: number; pending: number;
  lines: { srcLine: number; name: string; hsn: string; unit: string; gst: number; qty: number; rate: number; returned: number; reduced: number }[];
}
export interface NoteInput { kind: NoteKind; date: string; againstId: number; reason: string; narration?: string; lines: { srcLine: number; qty: number; diff?: number }[] }
export const getNoteDocs = (kind: NoteKind, party: number, excludeNote?: number) =>
  api.get<NoteDoc[]>('/accounts/books/note-docs', { params: { kind, party, excludeNote } }).then((r) => r.data);
export const createNote = (b: NoteInput) => api.post<VoucherFull>(`/accounts/books/vouchers/${encodeURIComponent(b.kind === 'dn' ? 'Debit Note' : 'Credit Note')}`, b).then((r) => r.data);
export const alterNote = (id: number, b: NoteInput) => api.put<VoucherFull>(`/accounts/books/vouchers/${id}`, b).then((r) => r.data);

export interface ItemOption { id: number; code: string; name: string; hsn: string; gst: number; unit: string; purchaseRate: number }

export const itemOptions = (q: string) => api.get<ItemOption[]>('/accounts/books/item-options', { params: { q } }).then((r) => r.data);
export const tradeSetup = () => api.get<{ company: string; gstin: string | null; stateCode: string | null }>('/accounts/books/trade-setup').then((r) => r.data);
export const purchasesYtd = (party: number, date: string, excludeVoucher?: number) =>
  api.get<{ before: number }>('/accounts/books/purchases-ytd', { params: { party, date, excludeVoucher } }).then((r) => r.data);
export const createTrade = (type: 'Sales' | 'Purchase', b: TradeInput) => api.post<VoucherFull>(`/accounts/books/vouchers/${type}`, b).then((r) => r.data);
export const alterTrade = (id: number, b: TradeInput) => api.put<VoucherFull>(`/accounts/books/vouchers/${id}`, b).then((r) => r.data);

export interface VoucherFull extends VoucherRow {
  /** Sales / Purchase item lines. */
  items: TradeItem[];
  lines: { ledgerId: number; ledger: string; side: 'Dr' | 'Cr'; amount: number; costCentreId?: number | null; bills: { refType: RefType; billNo: string | null; amount: number; dueDate: string | null }[] }[];
}

export const listVouchers = (type: VoucherType, q: { from?: string; to?: string }) =>
  api.get<VoucherRow[]>('/accounts/books/vouchers', { params: { type, ...q } }).then((r) => r.data);
export const getVoucher = (id: number) => api.get<VoucherFull>(`/accounts/books/vouchers/${id}`).then((r) => r.data);
export const createVoucher = (type: VoucherType, b: VoucherInput) => api.post<VoucherFull>(`/accounts/books/vouchers/${type}`, b).then((r) => r.data);
export const alterVoucher = (id: number, b: VoucherInput) => api.put<VoucherFull>(`/accounts/books/vouchers/${id}`, b).then((r) => r.data);
export const cancelVoucher = (id: number, reason: string) => api.post<VoucherFull>(`/accounts/books/vouchers/${id}/cancel`, { reason }).then((r) => r.data);

export interface EditLogRow { id: number; voucherId: number; version: number; action: 'Created' | 'Altered' | 'Cancelled'; at: string; by: string | null; snapshot: VoucherFull }
export const getEditLog = (q: { from?: string; to?: string; voucherId?: number }) =>
  api.get<EditLogRow[]>('/accounts/books/edit-log', { params: q }).then((r) => r.data);
